import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { BarcodeFormat, DecodeHintType } from "@zxing/library";
import "./KuotaPage.css";
import useFreshIdToken from "../auth/useFreshIdToken";
import { useLanguage } from "../i18n/LanguageContext";

const SIM_EMAIL_URL = "https://api.artamobile.id/v2/sim/email";

// SIM packaging carries the ICCID as a Code 128 barcode; some batches also print
// a QR. Narrowing the formats makes decoding noticeably faster on low-end phones.
const SCAN_FORMATS = [
    BarcodeFormat.CODE_128,
    BarcodeFormat.CODE_39,
    BarcodeFormat.QR_CODE,
    BarcodeFormat.DATA_MATRIX,
];

// Scans often carry spaces/dashes, and a QR may hold a URL ending in the ICCID.
// An ICCID is 18–20 digits and always starts with the 89 telecom major industry
// identifier. The quantifier is greedy on purpose: when 19 or 20 digits are
// present the longer form wins, so a full ICCID is never truncated to 18.
const extractIccid = (raw) => {
    const digits = String(raw || "").replace(/\D/g, "");
    const match = digits.match(/89\d{16,18}/);

    return match ? match[0] : "";
};

const isValidIccid = (value) => Boolean(extractIccid(value));

const SimGlyph = () => (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M5 4a2 2 0 012-2h7l5 5v13a2 2 0 01-2 2H7a2 2 0 01-2-2z" stroke="#237804" strokeWidth="1.8" strokeLinejoin="round" />
        <rect x="9" y="12" width="6" height="6" rx="1.5" stroke="#237804" strokeWidth="1.8" />
    </svg>
);

const LoginGlyph = () => (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M14 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4" stroke="#237804" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M10 8l-4 4 4 4M6 12h9" stroke="#237804" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const ScanGlyph = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M4 8V6a2 2 0 012-2h2M16 4h2a2 2 0 012 2v2M20 16v2a2 2 0 01-2 2h-2M8 20H6a2 2 0 01-2-2v-2" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
        <path d="M4 12h16" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
    </svg>
);

const StateTile = ({ icon, title, desc, children }) => (
    <div className="kt-state">
        <div className="kt-state-icon">{icon}</div>
        <h1 className="kt-state-title">{title}</h1>
        <p className="kt-state-desc">{desc}</p>
        {children}
    </div>
);

const KuotaPage = () => {
    const {
        isAuthenticated,
        isLoading,
        loginWithRedirect,
    } = useAuth0();
    const getFreshIdToken = useFreshIdToken();
    const { t } = useLanguage();
    const navigate = useNavigate();
    const { pathname, search } = useLocation();

    const [scanning, setScanning] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [manualIccid, setManualIccid] = useState("");
    const [showManual, setShowManual] = useState(false);

    const videoRef = useRef(null);
    const controlsRef = useRef(null);
    // Decoding fires continuously; this keeps the first hit from starting a
    // second submit while the request is still in flight.
    const claimedRef = useRef(false);

    // Flipping this off tears the reader down through the effect cleanup below,
    // which also covers navigating away mid-scan.
    const stopScanner = useCallback(() => setScanning(false), []);

    const claimIccid = useCallback(
        async (rawValue) => {
            const iccid = extractIccid(rawValue);

            if (!iccid) {
                setError(t("kuota.errors.unrecognized"));
                return;
            }

            if (claimedRef.current) return;
            claimedRef.current = true;

            stopScanner();
            setSubmitting(true);
            setError(null);

            try {
                // The ID token already carries a verified email claim, so the API
                // authorizes on it directly — no API-audience token needed.
                const token = await getFreshIdToken();

                if (!token) {
                    throw new Error(t("common.sessionExpired"));
                }

                const resp = await fetch(SIM_EMAIL_URL, {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({ iccid }),
                });

                if (resp.status === 401) {
                    throw new Error(t("common.sessionExpired"));
                }

                if (resp.status === 403) {
                    throw new Error(t("kuota.errors.unverified"));
                }

                if (resp.status === 404) {
                    throw new Error(t("kuota.errors.notFound"));
                }

                if (!resp.ok) {
                    throw new Error(t("kuota.errors.linkFailed"));
                }

                navigate(`/esims${search}`);
            } catch (e) {
                console.error("Failed to claim iccid", e);
                setError(e.message || t("common.genericError"));
                claimedRef.current = false;
                setSubmitting(false);
            }
        },
        [getFreshIdToken, navigate, search, stopScanner, t],
    );

    const startScanner = useCallback(() => {
        setError(null);
        claimedRef.current = false;
        setScanning(true);
    }, []);

    // The reader has to attach to a mounted <video>; starting it inside the
    // click handler would hand zxing a null ref, which it silently replaces with
    // a detached element — camera on, nothing on screen.
    useEffect(() => {
        if (!scanning || !videoRef.current) return undefined;

        let cancelled = false;
        const hints = new Map([[DecodeHintType.POSSIBLE_FORMATS, SCAN_FORMATS]]);
        const reader = new BrowserMultiFormatReader(hints);

        // deviceId undefined → zxing requests facingMode: 'environment' (rear camera).
        reader
            .decodeFromVideoDevice(undefined, videoRef.current, (result) => {
                if (result) claimIccid(result.getText());
            })
            .then((controls) => {
                if (cancelled) {
                    controls.stop();
                    return;
                }
                controlsRef.current = controls;
            })
            .catch((e) => {
                if (cancelled) return;
                console.error("Camera error", e);
                setScanning(false);
                setShowManual(true);
                setError(t("kuota.errors.camera"));
            });

        return () => {
            cancelled = true;
            controlsRef.current?.stop();
            controlsRef.current = null;
        };
    }, [scanning, claimIccid, t]);

    if (isLoading) {
        return (
            <div className="kt-page">
                <div className="kt-content kt-content-centered">
                    <div className="kt-spinner" aria-label={t("kuota.loadingAria")} />
                </div>
            </div>
        );
    }

    // Belum masuk — arahkan untuk login lebih dulu.
    if (!isAuthenticated) {
        return (
            <div className="kt-page">
                <div className="kt-content kt-content-centered">
                    <StateTile
                        icon={<LoginGlyph />}
                        title={t("kuota.loginTitle")}
                        desc={t("kuota.loginDesc")}
                    >
                        <button
                            className="kt-primary-btn"
                            onClick={() => {
                                localStorage.setItem("redirectAfterLogin", `${pathname}${search}`);
                                loginWithRedirect().catch((e) => console.error("Login error:", e));
                            }}
                        >
                            {t("common.login")}
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                <path d="M5 12h14M13 6l6 6-6 6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>
                    </StateTile>
                </div>
            </div>
        );
    }

    if (submitting) {
        return (
            <div className="kt-page">
                <div className="kt-content kt-content-centered">
                    <div className="kt-spinner" aria-label={t("kuota.linkingAria")} />
                    <p className="kt-state-desc kt-center">{t("kuota.linking")}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="kt-page">
            <div className={`kt-content${scanning ? "" : " kt-content-centered"}`}>
                {scanning ? (
                    <>
                        <h1 className="kt-scan-title">{t("kuota.scanTitle")}</h1>
                        <p className="kt-state-desc kt-center">
                            {t("kuota.scanDesc")}
                        </p>

                        <div className="kt-viewport">
                            <video ref={videoRef} className="kt-video" muted playsInline autoPlay />
                            <div className="kt-reticle" aria-hidden="true" />
                        </div>

                        <button className="kt-ghost-btn" onClick={stopScanner}>
                            {t("kuota.cancel")}
                        </button>
                    </>
                ) : (
                    <StateTile
                        icon={<SimGlyph />}
                        title={t("kuota.introTitle")}
                        desc={t("kuota.introDesc")}
                    >
                        <button className="kt-primary-btn" onClick={startScanner}>
                            <ScanGlyph />
                            {t("kuota.scanBtn")}
                        </button>
                        <button className="kt-link-btn" onClick={() => setShowManual((v) => !v)}>
                            {showManual ? t("kuota.hideManual") : t("kuota.showManual")}
                        </button>
                    </StateTile>
                )}

                {showManual && !scanning && (
                    <div className="kt-manual">
                        <label className="kt-label" htmlFor="kt-iccid">
                            {t("kuota.iccidLabel")}
                        </label>
                        <input
                            id="kt-iccid"
                            className="kt-input"
                            inputMode="numeric"
                            autoComplete="off"
                            placeholder="8962 0000 0000 0012 3456"
                            value={manualIccid}
                            onChange={(e) => setManualIccid(e.target.value)}
                        />
                        <button
                            className="kt-primary-btn"
                            disabled={!isValidIccid(manualIccid)}
                            onClick={() => claimIccid(manualIccid)}
                        >
                            {t("kuota.checkQuota")}
                        </button>
                    </div>
                )}

                {error && (
                    <p className="kt-error" role="alert">
                        {error}
                    </p>
                )}
            </div>
        </div>
    );
};

export default KuotaPage;
